import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { HiXCircle } from "react-icons/hi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { IResolveParams } from "reactjs-social-login";

// Types
import { EventProcessData, Event } from "../../types/EventPaymentTypes";

// Contexts & Hooks
import { useLmcUserView } from "../../contexts/AuthContext";
import { useUserSocialLogin } from "../../hooks/users";
import {
  useEventCardState,
  TicketTypeWithId,
} from "../../hooks/useEventCardState";

// Services
import PaymentModuleService from "../../services/paymentModule";
import EventService from "../../services/eventService";
import HttpService from "../../services/http";

// Utils
import { createErrorHandler } from "../../utils/errorHandler";
import { createGradientStyle } from "../../utils/colorUtils";
import { palette } from "../../utils/palette";

// Components
import LoyaltyCardContainer from "./LoyaltyCardContainer";
import BusinessCardFooter from "./BusinessCardFooter";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import { CustomButton } from "./CustomButton";
import {
  TicketCard,
  QuantitySelector,
  AuthView,
  EmailAuthView,
  MyTicketsView,
  PurchaseView,
  EntityBrand,
  ErrorView,
} from "./eventCard";

interface EventCardDisplayProps {
  processId: string;
  eventData: EventProcessData;
}

const EventCardDisplay: React.FC<EventCardDisplayProps> = ({
  processId,
  eventData,
}) => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const {
    dispatch,
    state: { auth },
  } = useLmcUserView();
  const { mutate: socialLogin } = useUserSocialLogin();

  // Extract event data
  const eventCode = eventData?.process_data?.event_code;
  const eventDetails = eventCode
    ? (eventData?.process_data?.[eventCode] as Event | undefined)
    : undefined;
  const eventSettings = eventDetails?.event_data?.settings;
  const entityData = eventData?.entity_data;

  const gradientStyle = useMemo(
    () => createGradientStyle(entityData?.accent_color || palette.accent),
    [entityData?.accent_color]
  );

  const availableTickets = useMemo<TicketTypeWithId[]>(() => {
    const ticketTypes = eventDetails?.event_data?.ticket_types || {};
    const soldTickets = eventDetails?.event_data?.sold || {};
    const availability = eventDetails?.event_data?.availability || {};

    if (
      !ticketTypes ||
      Object.keys(ticketTypes).length === 0 ||
      eventDetails?.active === false
    ) {
      return [];
    }

    const now = moment();
    const tickets: TicketTypeWithId[] = [];

    Object.entries(ticketTypes).forEach(([ticketId, ticket]) => {
      const settings = ticket.settings;
      const ticketAvailability = availability[ticketId];
      const quantitySold = ticketAvailability
        ? settings.quantity_total
          ? settings.quantity_total - (ticketAvailability.remaining || 0)
          : 0
        : soldTickets[ticketId] || 0;

      // Determine ticket status
      let status: TicketTypeWithId["status"] = "available";

      if (
        settings.sale_start_date &&
        now.isBefore(moment(settings.sale_start_date))
      ) {
        status = "coming_soon";
      } else if (
        settings.sale_end_date &&
        now.isAfter(moment(settings.sale_end_date))
      ) {
        status = "sale_ended";
      } else if (ticketAvailability) {
        if (!ticketAvailability.is_available) {
          status = "sold_out";
        }
      } else if (
        settings.quantity_total &&
        quantitySold >= settings.quantity_total
      ) {
        status = "sold_out";
      }

      tickets.push({
        ticket_id: ticketId,
        settings,
        quantity_sold: quantitySold,
        status,
      });
    });

    return tickets;
  }, [
    eventDetails?.event_data?.ticket_types,
    eventDetails?.event_data?.sold,
    eventDetails?.event_data?.availability,
  ]);

  // State management hook
  const state = useEventCardState({ availableTickets, t });

  // Track if we should go to my-tickets after auth
  const pendingMyTicketsRef = useRef(false);

  // Effect to fetch tickets after auth is confirmed (waits for state update)
  useEffect(() => {
    if (
      pendingMyTicketsRef.current &&
      auth.isAuthenticated &&
      auth.user_identification &&
      state.currentView === "my-tickets"
    ) {
      pendingMyTicketsRef.current = false;
      // Now auth context and SecureStorage are updated, safe to fetch
      const fetchTickets = async () => {
        const handleError = createErrorHandler({
          dispatch,
          navigate,
          redirectPath: `/event/${processId}`,
          onHandled: () => {
            state.resetAll();
            state.flipToFront();
          },
        });

        state.setIsLoadingTickets(true);

        try {
          const response = await HttpService.get(
            `/${auth.user_identification}/${entityData?.entity_identification}/event/my-tickets/${eventCode}`
          );
          state.setMyTickets(response?.data?.element?.tickets || []);
        } catch (error: unknown) {
          console.error("Error fetching tickets:", error);
          if (handleError(error)) return;
          toast.error(t("event.errors.loadTicketsFailed"));
          state.flipToFront();
        } finally {
          state.setIsLoadingTickets(false);
        }
      };

      fetchTickets();
    }
  }, [
    auth.isAuthenticated,
    auth.user_identification,
    state.currentView,
    dispatch,
    navigate,
    processId,
    entityData?.entity_identification,
    eventCode,
    state,
    t,
  ]);

  // Effect to navigate after social auth login is confirmed
  useEffect(() => {
    if (
      auth.isAuthenticated &&
      auth.user_identification &&
      state.currentView === "auth"
    ) {
      if (pendingMyTicketsRef.current) {
        state.flipToView("my-tickets");
      } else {
        state.flipToView("purchase");
      }
    }
  }, [auth.isAuthenticated, auth.user_identification, state]);

  // Calculate remaining tickets for selected ticket
  const getTicketRemaining = useCallback(
    (ticketId: string): number | null => {
      const ticket = availableTickets.find((t) => t.ticket_id === ticketId);
      if (!ticket) return null;

      const availability = eventDetails?.event_data?.availability?.[ticketId];
      return availability
        ? availability.remaining
        : ticket.settings.quantity_total
        ? ticket.settings.quantity_total - ticket.quantity_sold
        : null;
    },
    [availableTickets, eventDetails?.event_data?.availability]
  );

  // Handlers
  const handleProceedToAuth = useCallback(() => {
    if (!state.selectedTicketId) return;

    if (auth.isAuthenticated) {
      state.flipToBack("purchase");
    } else {
      state.flipToBack("auth");
    }
  }, [state, auth.isAuthenticated]);

  // Extracted ticket fetching logic
  const fetchMyTickets = useCallback(
    async (userIdentification?: string) => {
      const userId = userIdentification || auth.user_identification;

      if (!userId) {
        toast.error(t("event.errors.loginFirst"));
        state.flipToFront();
        return;
      }

      const handleError = createErrorHandler({
        dispatch,
        navigate,
        redirectPath: `/event/${processId}`,
        onHandled: () => {
          state.resetAll();
          state.flipToFront();
        },
      });

      state.setIsLoadingTickets(true);

      try {
        const response = await HttpService.get(
          `/${userId}/${entityData?.entity_identification}/event/my-tickets/${eventCode}`
        );
        state.setMyTickets(response?.data?.element?.tickets || []);
      } catch (error: unknown) {
        console.error("Error fetching tickets:", error);
        if (handleError(error)) return;
        toast.error(t("event.errors.loadTicketsFailed"));
        state.flipToFront();
      } finally {
        state.setIsLoadingTickets(false);
      }
    },
    [
      auth.user_identification,
      dispatch,
      navigate,
      processId,
      entityData?.entity_identification,
      eventCode,
      state,
      t,
    ]
  );

  const handleAuthSuccess = useCallback(() => {
    if (pendingMyTicketsRef.current) {
      // Don't reset the flag here - let the useEffect handle it after auth is confirmed
      state.flipToView("my-tickets");
    } else {
      state.flipToView("purchase");
    }
  }, [state]);

  const handleShowEmailAuth = useCallback(() => {
    state.flipToView("email-auth");
  }, [state]);

  const handleLogout = useCallback(() => {
    dispatch({ type: "LOGOUT", payload: {} });
    state.flipToFront();
    state.resetAll();
  }, [dispatch, state]);

  const handleViewMyTickets = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user_identification) {
      // User is not logged in, redirect to auth first
      pendingMyTicketsRef.current = true;
      state.flipToBack("auth");
      return;
    }

    // User is authenticated, fetch tickets directly
    state.flipToBack("my-tickets");
    await fetchMyTickets();
  }, [auth.isAuthenticated, auth.user_identification, state, fetchMyTickets]);

  const handleResolveSocialAuth = useCallback(
    (provider: IResolveParams["provider"], data: IResolveParams["data"]) => {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };

      socialLogin(
        {
          entity_identification: entityData?.entity_identification || "",
          data: { type: "login", providerPayload: cpPayload },
        },
        {
          onSuccess: (result) => {
            dispatch({ type: "LOGIN", payload: result.data.element });
            handleAuthSuccess();
          },
          onError: (error: { response?: { data?: { message?: string } } }) => {
            toast.error(
              error.response?.data?.message ||
                (t("loginPage.errors.generic") as string)
            );
          },
        }
      );
    },
    [
      socialLogin,
      entityData?.entity_identification,
      dispatch,
      handleAuthSuccess,
    ]
  );

  const handleRejectSocialAuth = useCallback(
    (err: { err: { error: string } }) => {
      console.log(err);
    },
    []
  );

  // Resend validation email state & handler
  const [resendingEmails, setResendingEmails] = useState<Set<string>>(
    new Set()
  );

  const handleResendValidation = useCallback(
    async (email: string) => {
      const entityId = entityData?.entity_identification || "";
      if (!entityId) return;

      setResendingEmails((prev) => new Set(prev).add(email));

      try {
        await PaymentModuleService.resendAttendeeValidationEmail(entityId, {
          user_email: email,
        });
        toast.success(t("event.purchase.validationEmailSent", { email }));
      } catch (error: unknown) {
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || t("event.errors.resendValidationFailed");
        toast.error(errorMessage);
      } finally {
        setResendingEmails((prev) => {
          const next = new Set(prev);
          next.delete(email);
          return next;
        });
      }
    },
    [entityData?.entity_identification, t]
  );

  // STEP 4: Validate attendees
  const handleValidateAttendees = useCallback(async () => {
    if (
      !state.selectedTicket ||
      !auth.isAuthenticated ||
      !auth.user_identification
    ) {
      toast.error(t("event.errors.loginFirst"));
      handleLogout();
      return;
    }

    // Check for duplicate emails among attendees
    if (state.watchedAttendees.length > 1) {
      const emails = state.watchedAttendees.map((a) =>
        a.user_email?.toLowerCase().trim()
      );
      const seen = new Set<string>();
      let hasDuplicates = false;

      emails.forEach((email, index) => {
        if (!email) return;
        if (seen.has(email)) {
          state.form.setError(`attendees.${index}.user_email`, {
            type: "manual",
            message: t("event.errors.duplicateEmails") as string,
          });
          hasDuplicates = true;
        }
        seen.add(email);
      });

      if (hasDuplicates) {
        toast.error(t("event.errors.duplicateEmails"));
        return;
      }
    }

    state.setIsProcessing(true);

    const handleError = createErrorHandler({
      dispatch,
      navigate,
      redirectPath: `/event/${processId}`,
      onHandled: () => {
        state.resetAll();
        state.flipToFront();
      },
    });

    try {
      const entityId = entityData?.entity_identification || "";

      const validateResponse = await PaymentModuleService.validateAttendees(
        entityId,
        {
          user_identification: auth.user_identification,
          event_code: eventCode || "",
          ticket_id: state.selectedTicketId!,
          additional_attendees:
            state.watchedAttendees.length > 0
              ? state.watchedAttendees
              : undefined,
        }
      );

      const validation = validateResponse.data?.element;

      if (!validation) {
        toast.error(t("event.errors.purchaseFailed"));
        return;
      }

      // Store validation result and advance to validated step
      // The UI in PurchaseView will display warnings for purchaser_already_has_ticket, etc.
      state.setValidationResult(validation);
      state.setPurchaseStep("validated");
    } catch (error: unknown) {
      console.error("Error validating attendees:", error);
      if (handleError(error)) return;
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("event.errors.purchaseFailed");
      toast.error(errorMessage);
    } finally {
      state.setIsProcessing(false);
    }
  }, [
    state,
    auth,
    entityData?.entity_identification,
    eventCode,
    dispatch,
    navigate,
    processId,
    t,
    handleLogout,
  ]);

  // STEP 5: Reserve tickets
  const handleReserveTickets = useCallback(async () => {
    if (!auth.user_identification || !state.validationResult) return;

    state.setIsProcessing(true);

    const handleError = createErrorHandler({
      dispatch,
      navigate,
      redirectPath: `/event/${processId}`,
      onHandled: () => {
        state.resetAll();
        state.flipToFront();
      },
    });

    try {
      const entityId = entityData?.entity_identification || "";
      const validation = state.validationResult;

      // Build attendee list: purchaser as plain email string, additional attendees as objects with names
      const attendeeList: Array<
        string | { user_email: string; first_name: string; last_name: string }
      > = [];
      if (
        !validation.purchaser_already_has_ticket &&
        validation.purchaser_email
      ) {
        attendeeList.push(validation.purchaser_email);
      }
      if (validation.valid_attendees) {
        validation.valid_attendees.forEach(
          (a: {
            user_email: string;
            first_name?: string;
            last_name?: string;
          }) => {
            const formAttendee = state.watchedAttendees.find(
              (wa) =>
                wa.user_email?.toLowerCase() === a.user_email?.toLowerCase()
            );
            attendeeList.push({
              user_email: a.user_email,
              first_name: formAttendee?.first_name || a.first_name || "",
              last_name: formAttendee?.last_name || a.last_name || "",
            });
          }
        );
      }

      const reserveResponse = await PaymentModuleService.reserveTickets(
        entityId,
        {
          user_identification: auth.user_identification,
          event_code: eventCode || "",
          ticket_id: state.selectedTicketId!,
          attendee_emails: attendeeList,
        }
      );

      const reservation = reserveResponse.data?.element;

      if (!reservation?.reservation_id) {
        toast.error(t("event.errors.reservationFailed"));
        return;
      }

      // Store reservation result and advance to next step
      state.setReservationResult(reservation);
      state.setPurchaseStep("reserved");
    } catch (error: unknown) {
      console.error("Error reserving tickets:", error);
      if (handleError(error)) return;
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("event.errors.purchaseFailed");
      toast.error(errorMessage);
    } finally {
      state.setIsProcessing(false);
    }
  }, [
    state,
    auth.user_identification,
    entityData?.entity_identification,
    eventCode,
    dispatch,
    navigate,
    processId,
    t,
  ]);

  // STEP 6: Proceed to payment
  const handleProceedToPayment = useCallback(async () => {
    if (!auth.user_identification || !state.reservationResult) return;

    state.setIsProcessing(true);

    const handleError = createErrorHandler({
      dispatch,
      navigate,
      redirectPath: `/event/${processId}`,
      onHandled: () => {
        state.resetAll();
        state.flipToFront();
      },
    });

    try {
      const entityId = entityData?.entity_identification || "";
      const reservation = state.reservationResult;
      const dashboardUrl = `${window.location.origin}/event/${processId}/dashboard?user_id=${auth.user_identification}`;

      const paymentResponse =
        await PaymentModuleService.createPaymentRequestForReservation(
          entityId,
          {
            user_identification: auth.user_identification,
            reservation_id: reservation.reservation_id,
            redirect_url: `${dashboardUrl}&reservation_id=${reservation.reservation_id}`,
          }
        );

      const paymentResult = paymentResponse.data?.element;

      // Handle free tickets (registered immediately)
      if (paymentResult?.tickets_registered) {
        navigate(`/event/${processId}/dashboard`, {
          state: {
            ticketData: {
              ticket_id: state.selectedTicketId!,
              event_code: eventCode || "",
              payment_date: new Date().toISOString(),
              ticket_quantity: paymentResult.quantity,
              payment_amount: 0,
              reservation_id: reservation.reservation_id,
            },
          },
        });
        return;
      }

      // Paid tickets - redirect to Stripe payment
      const paymentUrl =
        paymentResult?.payment_link || paymentResult?.payment_url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        toast.error(t("event.errors.paymentFailed"));
      }
    } catch (error: unknown) {
      console.error("Error creating payment:", error);
      if (handleError(error)) return;
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("event.errors.purchaseFailed");
      toast.error(errorMessage);
    } finally {
      state.setIsProcessing(false);
    }
  }, [
    state,
    auth.user_identification,
    entityData?.entity_identification,
    eventCode,
    dispatch,
    navigate,
    processId,
    t,
  ]);

  // Render error view if no data
  if (!eventSettings || !entityData) {
    return <ErrorView t={t} />;
  }

  // Front Content - Ticket Selection
  const frontContent = (
    <div className="w-full h-full" translate="no">
      <div className="w-full h-full min-h-0 relative rounded-xl overflow-hidden">
        {/* Main Content */}
        <div className="row-start-1 row-end-2 min-h-0">
          <div
            className="h-full overflow-y-auto touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* Entity Header */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <EntityBrand
                          logo={entityData.entity_logo}
                          name={entityData.entity_name}
                          fallbackName={eventSettings?.event_name}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h1 className="text-xl sm:text-2xl font-semibold text-white leading-tight">
                          {entityData.entity_name}
                        </h1>
                        {entityData.entity_description && (
                          <p className="text-sm text-white/70 mt-1">
                            {entityData.entity_description}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <LanguageSelector variant="compact" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Details Section */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
              {eventSettings.event_image_url && (
                <div className="w-full aspect-video">
                  <img
                    src={eventSettings.event_image_url}
                    alt={eventSettings.event_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="relative px-5 pt-5 pb-6 sm:px-6 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-white leading-tight">
                      {eventSettings.event_name}
                    </h2>
                    {eventSettings.event_description && (
                      <div className="mt-1">
                        <ExpandableDescription
                          content={eventSettings.event_description}
                          maxLength={150}
                          maxLines={3}
                          textClassName="text-sm text-white/70 leading-relaxed"
                          buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-white/80 hover:text-white transition-colors duration-200"
                          expandText={t("key97") as string}
                          collapseText={t("key107") as string}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets Section */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-white/20 bg-gray-950/90 backdrop-blur m-3 sm:m-4">
              <div className="relative px-2 pt-5 pb-6 sm:px-2 sm:pt-6 sm:pb-7 flex flex-col">
                <div
                  className="absolute inset-0 opacity-90"
                  style={gradientStyle}
                />
                <div className="relative z-10 flex flex-col gap-6 w-full">
                  <div className="space-y-2" dir="auto">
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                      {t("event.tickets.title")}
                    </p>

                    {availableTickets.length === 0 ? (
                      <div className="text-center py-12">
                        <HiXCircle
                          className="mx-auto text-white/40 mb-4"
                          size={64}
                        />
                        <h2 className="text-xl font-semibold text-white mb-2">
                          {t("event.tickets.noTickets")}
                        </h2>
                        <p className="text-white/70 text-sm">
                          {t("event.tickets.noTicketsMessage")}
                        </p>
                      </div>
                    ) : state.selectedTicketId && state.selectedTicket ? (
                      <QuantitySelector
                        ticket={state.selectedTicket}
                        quantity={state.ticketQuantity}
                        remaining={getTicketRemaining(state.selectedTicketId)}
                        onQuantityChange={state.setTicketQuantity}
                        onProceed={handleProceedToAuth}
                        onBack={() =>
                          state.selectTicket(state.selectedTicketId!)
                        }
                        t={t}
                      />
                    ) : (
                      <>
                        {availableTickets.map((ticket) => (
                          <TicketCard
                            key={ticket.ticket_id}
                            ticket={ticket}
                            isSelected={
                              state.selectedTicketId === ticket.ticket_id
                            }
                            remaining={getTicketRemaining(ticket.ticket_id)}
                            onSelect={state.selectTicket}
                            t={t}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* My Tickets Button */}
        <div className="row-start-2 row-end-3 overflow-hidden">
          <div className="px-3 sm:px-4 pb-3 sm:pb-4 relative z-10">
            <CustomButton
              title={t("event.tickets.myTickets")}
              onClick={handleViewMyTickets}
              variant="secondary"
              className="w-full bg-gray-800/90 border-white/20 text-white enabled:hover:bg-gray-700/90"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="row-start-3 row-end-4">
          <BusinessCardFooter showFooter={true} theme="default" />
        </div>
      </div>
    </div>
  );

  // Back content based on current view
  const getBackContent = () => {
    switch (state.currentView) {
      case "auth":
        return (
          <AuthView
            onBack={state.flipToFront}
            onShowEmailAuth={handleShowEmailAuth}
            onResolveSocialAuth={handleResolveSocialAuth}
            onRejectSocialAuth={handleRejectSocialAuth}
            t={t}
          />
        );
      case "email-auth":
        return (
          <EmailAuthView
            onBack={state.flipToFront}
            onLoginSuccess={handleAuthSuccess}
            t={t}
          />
        );
      case "my-tickets":
        return (
          <MyTicketsView
            myTickets={state.myTickets}
            isLoading={state.isLoadingTickets}
            availableTickets={availableTickets}
            eventName={eventSettings?.event_name}
            eventLocation={eventSettings?.location}
            userEmail={auth.user_email}
            onBack={state.flipToFront}
            onLogout={handleLogout}
            t={t}
          />
        );
      case "purchase":
        return (
          <PurchaseView
            selectedTicket={state.selectedTicket}
            ticketQuantity={state.ticketQuantity}
            userEmail={auth.user_email}
            form={state.form}
            fields={state.fieldArray.fields}
            watchedAttendees={state.watchedAttendees}
            expandedAttendees={state.expandedAttendees}
            onToggleExpanded={state.toggleAttendeeExpanded}
            onExpandAttendees={state.expandAttendees}
            purchaseStep={state.purchaseStep}
            validationResult={state.validationResult}
            reservationResult={state.reservationResult}
            isProcessing={state.isProcessing}
            onBack={state.flipToFront}
            onLogout={handleLogout}
            onValidate={handleValidateAttendees}
            onReserve={handleReserveTickets}
            onPay={handleProceedToPayment}
            onEditAttendees={() => state.setPurchaseStep("form")}
            onResendValidation={handleResendValidation}
            resendingEmails={resendingEmails}
            onReservationExpired={async () => {
              const reservationId = state.reservationResult?.reservation_id;
              if (
                reservationId &&
                entityData?.entity_identification &&
                auth.user_identification
              ) {
                try {
                  await EventService.cancelReservation(
                    auth.user_identification,
                    entityData.entity_identification,
                    reservationId
                  );
                } catch (err) {
                  console.warn("Failed to cancel expired reservation:", err);
                }
              }
              toast.error(
                t("event.errors.reservationExpired") ||
                  "Reservation expired. Please try again."
              );
              state.resetAll();
              state.flipToFront();
            }}
            t={t}
          />
        );
      default:
        return null;
    }
  };

  return (
    <LoyaltyCardContainer
      frontContent={frontContent}
      backContent={getBackContent()}
      isPreview={false}
      isFlipping={state.isFlipping}
      showEmailLogin={state.showBackFace}
      onFlip={state.setShowBackFace}
      className="max-w-[360px] w-full"
      fullHeight={true}
    />
  );
};

export default EventCardDisplay;
